/* eslint-disable */
import React from "react";
import { supabase } from "./supabaseClient";

const C = {
  primary:"#1a6b3c", primaryPale:"#e8f5ee", primaryMid:"#6ee7b7",
  amber:"#d97706",   amberPale:"#fffbeb",   amberMid:"#fde68a",
  rose:"#e11d48",    rosePale:"#fef2f2",
  blue:"#2563eb",    bluePale:"#eff6ff",
  teal:"#0d9488",    tealPale:"#f0fdfa",
  text:"#111827",    muted:"#6b7280",       border:"#d1fae5",
  white:"#ffffff",   bg:"#f9fafb",          shadow:"rgba(0,0,0,0.06)",
};

const PLAN_STATUS_META = {
  draft:     { label:"Draft",     bg:"#f3f4f6", fg:"#4b5563", bd:"#e5e7eb", icon:"📝" },
  submitted: { label:"Submitted", bg:"#fffbeb", fg:"#d97706", bd:"#fde68a", icon:"⏳" },
  approved:  { label:"Approved",  bg:"#f0fdf4", fg:"#16a34a", bd:"#bbf7d0", icon:"✅" },
  rejected:  { label:"Rejected",  bg:"#fef2f2", fg:"#dc2626", bd:"#fecaca", icon:"❌" },
};

const TOPIC_STATUS_META = {
  pending:     { label:"Pending",     bg:"#f3f4f6", fg:"#4b5563", icon:"⬜" },
  in_progress: { label:"In Progress", bg:"#eff6ff", fg:"#2563eb", icon:"🔵" },
  completed:   { label:"Completed",   bg:"#f0fdf4", fg:"#16a34a", icon:"✅" },
};

const todayStr = () => new Date().toISOString().split("T")[0];
const fmt = d => d ? new Date(d+"T00:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "—";
const defaultAcademicYear = () => {
  const y = new Date().getFullYear();
  const m = new Date().getMonth();
  const start = m >= 6 ? y : y - 1; // school year assumed to start ~July
  return `${start}–${start+1}`;
};

const isOverdue = (t) => t.status !== "completed" && t.planned_end_date && t.planned_end_date < todayStr();

function paceFor(topics) {
  const total = topics.length;
  const done  = topics.filter(t=>t.status==="completed").length;
  const overdue = topics.filter(isOverdue).length;
  return { total, done, overdue, behind: overdue > 0 };
}

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div style={{position:"fixed",top:20,right:20,zIndex:9999,background:toast.ok?"#f0fdf4":"#fef2f2",color:toast.ok?"#16a34a":"#dc2626",border:`1px solid ${toast.ok?"#bbf7d0":"#fecaca"}`,borderRadius:10,padding:"10px 18px",fontWeight:700,fontSize:13,boxShadow:"0 4px 18px rgba(0,0,0,.15)"}}>
      {toast.msg}
    </div>
  );
}

function PaceBadge({ topics }) {
  const { total, done, behind } = paceFor(topics);
  if (total === 0) return <span style={{fontSize:11,color:C.muted}}>No topics yet</span>;
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,fontWeight:700,
      color:behind?C.rose:C.primary, background:behind?C.rosePale:C.primaryPale,
      border:`1px solid ${behind?"#fecaca":C.border}`, borderRadius:6,padding:"2px 9px"}}>
      {behind ? "⚠️ Behind schedule" : "🎯 On track"} · {done}/{total} done
    </span>
  );
}

function ProgressBar({ topics }) {
  const { total, done, overdue, behind } = paceFor(topics);
  const pct = total ? Math.round((done/total)*100) : 0;
  return (
    <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:9,padding:"10px 14px",marginBottom:4}}>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>
        <span>{done} of {total} topics completed ({pct}%)</span>
        {overdue>0 && <span style={{color:C.rose}}>⚠️ {overdue} overdue</span>}
      </div>
      <div style={{background:"#f3f4f6",borderRadius:4,height:8,overflow:"hidden"}}>
        <div style={{background:behind?C.rose:C.primary,height:"100%",width:`${pct}%`,transition:"width .4s"}}/>
      </div>
    </div>
  );
}

// Compact bar for collapsed plan headers — so pace is visible without expanding
function MiniBar({ topics }) {
  const { total, done, behind } = paceFor(topics);
  if (!total) return null;
  const pct = Math.round((done/total)*100);
  return (
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <div style={{width:60,background:"#f3f4f6",borderRadius:4,height:6,overflow:"hidden"}}>
        <div style={{background:behind?C.rose:C.primary,height:"100%",width:`${pct}%`}}/>
      </div>
      <span style={{fontSize:11,color:C.muted,fontWeight:700}}>{pct}%</span>
    </div>
  );
}

// ── Topic row ────────────────────────────────────────────────────────────────
function TopicRow({ topic, editable, canEditContent, onStatusChange, onDelete, onEdit }) {
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState(null);
  const [confirmingDone, setConfirmingDone] = React.useState(false);
  const [doneNote, setDoneNote] = React.useState("");
  const st = TOPIC_STATUS_META[topic.status] || TOPIC_STATUS_META.pending;
  const overdue = isOverdue(topic);

  const startEdit = () => { setForm({ title:topic.title, planned_start_date:topic.planned_start_date||"", planned_end_date:topic.planned_end_date||"", description:topic.description||"" }); setEditing(true); };
  const save = () => { if(!form.title.trim()) return; onEdit(topic.id, form); setEditing(false); };
  const confirmDone = () => { onStatusChange(topic.id,"completed",doneNote.trim()||null); setConfirmingDone(false); setDoneNote(""); };

  if (confirmingDone) {
    return (
      <div style={{display:"flex",flexDirection:"column",gap:8,padding:"10px 12px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:9}}>
        <div style={{fontSize:12,fontWeight:700,color:"#16a34a"}}>Mark "{topic.title}" as completed</div>
        <input placeholder="Note for this topic (optional)" value={doneNote} onChange={e=>setDoneNote(e.target.value)} autoFocus
          style={{padding:"7px 10px",borderRadius:7,border:"1px solid #bbf7d0",fontSize:12,boxSizing:"border-box"}}/>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={()=>{setConfirmingDone(false);setDoneNote("");}} style={{background:C.white,color:C.muted,border:`1px solid ${C.border}`,borderRadius:7,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Cancel</button>
          <button onClick={confirmDone} style={{background:"#16a34a",color:C.white,border:"none",borderRadius:7,padding:"6px 16px",fontSize:11,fontWeight:700,cursor:"pointer"}}>✅ Confirm Done</button>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div style={{display:"flex",flexDirection:"column",gap:8,padding:"10px 12px",background:C.bg,border:`1px dashed ${C.border}`,borderRadius:9}}>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}
            style={{flex:1,minWidth:160,padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12}}/>
          <input type="date" value={form.planned_start_date} onChange={e=>setForm(p=>({...p,planned_start_date:e.target.value}))}
            style={{padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12}}/>
          <input type="date" value={form.planned_end_date} onChange={e=>setForm(p=>({...p,planned_end_date:e.target.value}))}
            style={{padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12}}/>
        </div>
        <input placeholder="Notes / learning objective (optional)" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))}
          style={{padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12,boxSizing:"border-box"}}/>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={()=>setEditing(false)} style={{background:C.white,color:C.muted,border:`1px solid ${C.border}`,borderRadius:7,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Cancel</button>
          <button onClick={save} style={{background:C.primary,color:C.white,border:"none",borderRadius:7,padding:"6px 16px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Save</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",
      background:overdue?C.rosePale:C.white, border:`1px solid ${overdue?"#fecaca":C.border}`,borderRadius:9,flexWrap:"wrap"}}>
      <span style={{background:st.bg,color:st.fg,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,flexShrink:0}}>
        {st.icon} {st.label}
      </span>
      <div style={{flex:1,minWidth:140}}>
        <div style={{fontWeight:700,fontSize:13,color:C.text}}>{topic.title}</div>
        <div style={{fontSize:11,color:overdue?C.rose:C.muted,fontWeight:overdue?700:400}}>
          {fmt(topic.planned_start_date)} → {fmt(topic.planned_end_date)}
          {overdue && " · overdue"}
        </div>
        {topic.description && <div style={{fontSize:11,color:C.muted,marginTop:2,fontStyle:"italic"}}>{topic.description}</div>}
        {topic.status==="completed" && topic.completion_note && (
          <div style={{fontSize:11,color:"#16a34a",marginTop:2}}>✓ {topic.completion_note}</div>
        )}
      </div>
      <div style={{display:"flex",gap:6,flexShrink:0}}>
        {canEditContent && (
          <button onClick={startEdit} title="Edit topic"
            style={{background:C.bg,color:C.muted,border:`1px solid ${C.border}`,borderRadius:7,padding:"4px 9px",fontSize:11,cursor:"pointer"}}>✏️</button>
        )}
        {editable && topic.status !== "in_progress" && topic.status !== "completed" && (
          <button onClick={()=>onStatusChange(topic.id,"in_progress")}
            style={{background:C.bluePale,color:C.blue,border:"1px solid #bfdbfe",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Start</button>
        )}
        {editable && topic.status !== "completed" && (
          <button onClick={()=>setConfirmingDone(true)}
            style={{background:"#f0fdf4",color:"#16a34a",border:"1px solid #bbf7d0",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Mark Done</button>
        )}
        {editable && topic.status === "completed" && (
          <button onClick={()=>onStatusChange(topic.id,"pending")}
            style={{background:C.bg,color:C.muted,border:`1px solid ${C.border}`,borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Reopen</button>
        )}
        {editable && (
          <button onClick={()=>onDelete(topic.id)}
            style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:7,padding:"4px 9px",fontSize:11,cursor:"pointer"}}>✕</button>
        )}
      </div>
    </div>
  );
}

// ── Add-topic inline form ───────────────────────────────────────────────────
function AddTopicForm({ onAdd }) {
  const [form, setForm] = React.useState({ title:"", planned_start_date:"", planned_end_date:"", description:"" });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const submit = () => {
    if (!form.title.trim()) return;
    onAdd(form);
    setForm({ title:"", planned_start_date:"", planned_end_date:"", description:"" });
  };
  return (
    <div style={{display:"flex",flexDirection:"column",gap:8,background:C.bg,borderRadius:9,padding:"10px 12px",border:`1px dashed ${C.border}`}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <input placeholder="Topic / chapter title" value={form.title} onChange={e=>set("title",e.target.value)}
          style={{flex:1,minWidth:160,padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12}}/>
        <input type="date" value={form.planned_start_date} onChange={e=>set("planned_start_date",e.target.value)}
          style={{padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12}}/>
        <input type="date" value={form.planned_end_date} onChange={e=>set("planned_end_date",e.target.value)}
          style={{padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12}}/>
        <button onClick={submit}
          style={{background:C.primary,color:C.white,border:"none",borderRadius:7,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          + Add Topic
        </button>
      </div>
      <input placeholder="Notes / learning objective (optional)" value={form.description} onChange={e=>set("description",e.target.value)}
        style={{padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12,boxSizing:"border-box"}}/>
    </div>
  );
}

// ── New / edit plan form ─────────────────────────────────────────────────────
function NewPlanForm({ subjects, classes, onCreate, onCancel, initial=null, title="New Course Plan", submitLabel="Create Plan" }) {
  const [form, setForm] = React.useState({
    subject_name: initial?.subject_name || subjects[0]||"", class: initial?.class || classes[0]||"",
    term_name: initial?.term_name || "Term 1", academic_year: initial?.academic_year || defaultAcademicYear(), notes:"",
  });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  return (
    <div style={{background:C.white,borderRadius:12,padding:16,border:`1px solid ${C.border}`,display:"flex",flexDirection:"column",gap:10}}>
      <div style={{fontWeight:800,fontSize:14,color:C.primary}}>{title}</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:C.muted}}>SUBJECT</label>
          {subjects.length ? (
            <select value={form.subject_name} onChange={e=>set("subject_name",e.target.value)}
              style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13}}>
              {subjects.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <input value={form.subject_name} onChange={e=>set("subject_name",e.target.value)} placeholder="e.g. Mathematics"
              style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,boxSizing:"border-box"}}/>
          )}
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:C.muted}}>CLASS</label>
          {classes.length ? (
            <select value={form.class} onChange={e=>set("class",e.target.value)}
              style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13}}>
              {classes.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <input value={form.class} onChange={e=>set("class",e.target.value)} placeholder="e.g. Class 5"
              style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,boxSizing:"border-box"}}/>
          )}
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:C.muted}}>TERM</label>
          <input value={form.term_name} onChange={e=>set("term_name",e.target.value)}
            style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,boxSizing:"border-box"}}/>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:C.muted}}>ACADEMIC YEAR</label>
          <input value={form.academic_year} onChange={e=>set("academic_year",e.target.value)}
            style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,boxSizing:"border-box"}}/>
        </div>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button onClick={onCancel} style={{background:C.white,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Cancel</button>
        <button onClick={()=>onCreate(form)} disabled={!form.subject_name||!form.class}
          style={{background:C.primary,color:C.white,border:"none",borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

// ── Teacher-facing tab (used inside TeacherPortal) ──────────────────────────
export function CoursePlanTeacherTab({ teacher, subjects = [], classes = [] }) {
  const [plans, setPlans] = React.useState([]);
  const [topicsByPlan, setTopicsByPlan] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [toast, setToast] = React.useState(null);
  const [showNewForm, setShowNewForm] = React.useState(false);
  const [expanded, setExpanded] = React.useState(null);
  const [editingPlan, setEditingPlan] = React.useState(null);

  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  const load = React.useCallback(async () => {
    setLoading(true);
    const { data: planRows } = await supabase.from("course_plans").select("*")
      .eq("teacher_name", teacher.name).order("created_at",{ascending:false});
    setPlans(planRows||[]);
    if ((planRows||[]).length) {
      const { data: topicRows } = await supabase.from("course_plan_topics").select("*")
        .in("plan_id", planRows.map(p=>p.id)).order("sort_order");
      const grouped = {};
      (topicRows||[]).forEach(t => { (grouped[t.plan_id] ||= []).push(t); });
      setTopicsByPlan(grouped);
    } else {
      setTopicsByPlan({});
    }
    setLoading(false);
  }, [teacher.name]);

  React.useEffect(()=>{ load(); }, [load]);

  const createPlan = async (form) => {
    const dup = plans.find(p=>p.subject_name===form.subject_name && p.class===form.class &&
      p.term_name===form.term_name && p.academic_year===form.academic_year);
    if (dup) { showToast("A plan already exists for this subject/class/term — opening it."); setExpanded(dup.id); setShowNewForm(false); return; }
    const { data, error } = await supabase.from("course_plans").insert({
      ...form, teacher_id: teacher.staff_id||null, teacher_name: teacher.name, status:"draft",
    }).select().single();
    if (error) { showToast("Error: "+error.message, false); return; }
    setPlans(p=>[data,...p]);
    setTopicsByPlan(p=>({...p,[data.id]:[]}));
    setShowNewForm(false);
    setExpanded(data.id);
    showToast("Plan created — now add your topics.");
  };

  const addTopic = async (planId, form) => {
    const existing = topicsByPlan[planId]||[];
    const { data, error } = await supabase.from("course_plan_topics").insert({
      plan_id: planId, sort_order: existing.length, title: form.title,
      planned_start_date: form.planned_start_date||null, planned_end_date: form.planned_end_date||null,
      description: form.description||null, status:"pending",
    }).select().single();
    if (error) { showToast("Error: "+error.message, false); return; }
    setTopicsByPlan(p=>({...p,[planId]:[...(p[planId]||[]),data]}));
  };

  const updateTopicStatus = async (topicId, status, note) => {
    const extra = status==="completed"
      ? {completed_at: todayStr(), completion_note: note||null}
      : {completed_at:null, completion_note:null};
    const { error } = await supabase.from("course_plan_topics").update({status,...extra}).eq("id",topicId);
    if (error) { showToast("Error: "+error.message, false); return; }
    setTopicsByPlan(p=>{
      const next = {...p};
      Object.keys(next).forEach(planId => { next[planId] = next[planId].map(t=>t.id===topicId?{...t,status,...extra}:t); });
      return next;
    });
  };

  const deleteTopic = async (topicId) => {
    const { error } = await supabase.from("course_plan_topics").delete().eq("id",topicId);
    if (error) { showToast("Error: "+error.message, false); return; }
    setTopicsByPlan(p=>{
      const next = {...p};
      Object.keys(next).forEach(planId => { next[planId] = next[planId].filter(t=>t.id!==topicId); });
      return next;
    });
  };

  const updateTopic = async (topicId, fields) => {
    const payload = { title:fields.title, planned_start_date:fields.planned_start_date||null,
      planned_end_date:fields.planned_end_date||null, description:fields.description||null };
    const { error } = await supabase.from("course_plan_topics").update(payload).eq("id",topicId);
    if (error) { showToast("Error: "+error.message, false); return; }
    setTopicsByPlan(p=>{
      const next = {...p};
      Object.keys(next).forEach(planId => { next[planId] = next[planId].map(t=>t.id===topicId?{...t,...payload}:t); });
      return next;
    });
    showToast("Topic updated.");
  };

  const updatePlanHeader = async (planId, form) => {
    const payload = { subject_name:form.subject_name, class:form.class, term_name:form.term_name, academic_year:form.academic_year };
    const { error } = await supabase.from("course_plans").update(payload).eq("id",planId);
    if (error) { showToast("Error: "+error.message, false); return; }
    setPlans(p=>p.map(x=>x.id===planId?{...x,...payload}:x));
    setEditingPlan(null);
    showToast("Plan details updated.");
  };

  const submitPlan = async (plan) => {
    const topics = topicsByPlan[plan.id]||[];
    if (!topics.length) { showToast("Add at least one topic before submitting.", false); return; }
    const { error } = await supabase.from("course_plans").update({
      status:"submitted", submitted_at:new Date().toISOString(),
    }).eq("id",plan.id);
    if (error) { showToast("Error: "+error.message, false); return; }
    setPlans(p=>p.map(x=>x.id===plan.id?{...x,status:"submitted",submitted_at:new Date().toISOString()}:x));
    showToast("Plan submitted for admin approval.");
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <Toast toast={toast}/>
      <div style={{background:C.white,borderRadius:14,padding:"18px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 10px ${C.shadow}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontWeight:900,fontSize:17,color:C.primary,marginBottom:4}}>📘 Course Plan & Syllabus Tracker</div>
          <div style={{fontSize:12,color:C.muted}}>Plan your topics, track your pace, and submit for approval.</div>
        </div>
        {!showNewForm && <button onClick={()=>setShowNewForm(true)}
          style={{background:C.primary,color:C.white,border:"none",borderRadius:9,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
          + New Plan
        </button>}
      </div>

      {showNewForm && <NewPlanForm subjects={subjects} classes={classes} onCreate={createPlan} onCancel={()=>setShowNewForm(false)}/>}

      {loading && <div style={{color:C.muted,fontSize:13,padding:16}}>Loading…</div>}
      {!loading && plans.length===0 && !showNewForm && (
        <div style={{background:C.white,borderRadius:12,padding:30,textAlign:"center",color:C.muted,fontSize:13,border:`1px solid ${C.border}`}}>
          No course plans yet. Click "+ New Plan" to lay out your syllabus for a subject/class.
        </div>
      )}

      {plans.map(plan => {
        const topics = topicsByPlan[plan.id]||[];
        const st = PLAN_STATUS_META[plan.status]||PLAN_STATUS_META.draft;
        const open = expanded===plan.id;
        const isEditingHeader = editingPlan===plan.id;
        return (
          <div key={plan.id} style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
            <div onClick={()=>setExpanded(open?null:plan.id)}
              style={{padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",cursor:"pointer"}}>
              <div>
                <div style={{fontWeight:800,fontSize:14,color:C.text}}>{plan.subject_name} · {plan.class}</div>
                <div style={{fontSize:11,color:C.muted}}>{plan.term_name} · {plan.academic_year}</div>
              </div>
              <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                <PaceBadge topics={topics}/>
                <MiniBar topics={topics}/>
                <span style={{background:st.bg,color:st.fg,border:`1px solid ${st.bd}`,borderRadius:6,padding:"2px 9px",fontSize:11,fontWeight:700}}>
                  {st.icon} {st.label}
                </span>
                {(plan.status==="draft"||plan.status==="rejected") && (
                  <button onClick={(e)=>{e.stopPropagation(); setExpanded(plan.id); setEditingPlan(isEditingHeader?null:plan.id);}}
                    title="Edit plan details"
                    style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"2px 8px",fontSize:12,cursor:"pointer"}}>
                    ✏️
                  </button>
                )}
                <span style={{fontSize:13,color:C.muted}}>{open?"▲":"▼"}</span>
              </div>
            </div>
            {open && (
              <div style={{padding:"0 18px 18px",display:"flex",flexDirection:"column",gap:8}}>
                {plan.status==="rejected" && plan.notes && (
                  <div style={{background:C.rosePale,border:"1px solid #fecaca",borderRadius:8,padding:"8px 12px",fontSize:12,color:C.rose}}>
                    ❌ Rejected: {plan.notes}
                  </div>
                )}
                {isEditingHeader ? (
                  <NewPlanForm subjects={subjects} classes={classes} initial={plan} title="Edit Plan Details" submitLabel="Save Changes"
                    onCreate={(form)=>updatePlanHeader(plan.id, form)} onCancel={()=>setEditingPlan(null)}/>
                ) : (
                  <>
                    {topics.length>0 && <ProgressBar topics={topics}/>}
                    {topics.map(t => (
                      <TopicRow key={t.id} topic={t} editable canEditContent onStatusChange={updateTopicStatus} onDelete={deleteTopic} onEdit={updateTopic}/>
                    ))}
                    <AddTopicForm onAdd={(form)=>addTopic(plan.id, form)}/>
                    {(plan.status==="draft"||plan.status==="rejected") && (
                      <div style={{display:"flex",justifyContent:"flex-end"}}>
                        <button onClick={()=>submitPlan(plan)}
                          style={{background:C.amber,color:C.white,border:"none",borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                          📤 Submit for Approval
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Admin review (used as a top-level QKAcademy tab) ────────────────────────
export function CoursePlanAdmin() {
  const [plans, setPlans] = React.useState([]);
  const [topicsByPlan, setTopicsByPlan] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [toast, setToast] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("submitted");
  const [teacherFilter, setTeacherFilter] = React.useState("all");
  const [classFilter, setClassFilter] = React.useState("all");
  const [expanded, setExpanded] = React.useState(null);
  const [editingPlan, setEditingPlan] = React.useState(null);
  const [view, setView] = React.useState("plans"); // "plans" | "overview"
  const [overviewBy, setOverviewBy] = React.useState("teacher"); // "teacher" | "class"
  const [decidingFor, setDecidingFor] = React.useState(null); // plan id
  const [decidingType, setDecidingType] = React.useState(null); // "approved" | "rejected"
  const [decisionNote, setDecisionNote] = React.useState("");

  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  const load = React.useCallback(async () => {
    setLoading(true);
    const { data: planRows } = await supabase.from("course_plans").select("*").order("created_at",{ascending:false});
    setPlans(planRows||[]);
    if ((planRows||[]).length) {
      const { data: topicRows } = await supabase.from("course_plan_topics").select("*")
        .in("plan_id", planRows.map(p=>p.id)).order("sort_order");
      const grouped = {};
      (topicRows||[]).forEach(t => { (grouped[t.plan_id] ||= []).push(t); });
      setTopicsByPlan(grouped);
      alertBehindPlans(planRows, grouped);
    }
    setLoading(false);
  }, []);

  // Notify admin (once per day per plan) when a plan has overdue topics —
  // closes the loop so falling behind doesn't go unnoticed until someone opens this tab.
  const alertBehindPlans = async (planRows, grouped) => {
    const behindPlans = planRows.filter(p => p.status!=="rejected" && paceFor(grouped[p.id]||[]).behind);
    if (!behindPlans.length) return;
    const startOfToday = new Date(); startOfToday.setHours(0,0,0,0);
    const { data: already } = await supabase.from("notifications").select("source_id")
      .eq("source_type","course_plans").in("source_id", behindPlans.map(p=>p.id))
      .gte("created_at", startOfToday.toISOString());
    const alreadyIds = new Set((already||[]).map(n=>n.source_id));
    const toAlert = behindPlans.filter(p=>!alreadyIds.has(p.id));
    if (!toAlert.length) return;
    await supabase.from("notifications").insert(toAlert.map(p => ({
      title: "⚠️ Course Plan Behind Schedule",
      body: `${p.teacher_name} is behind on ${p.subject_name} · ${p.class} (${p.term_name})`,
      type: "alert", target_role: "admin",
      source_id: p.id, source_type: "course_plans", created_by: "System",
    })));
  };

  React.useEffect(()=>{ load(); }, [load]);

  const updateTopic = async (topicId, fields) => {
    const payload = { title:fields.title, planned_start_date:fields.planned_start_date||null,
      planned_end_date:fields.planned_end_date||null, description:fields.description||null };
    const { error } = await supabase.from("course_plan_topics").update(payload).eq("id",topicId);
    if (error) { showToast("Error: "+error.message, false); return; }
    setTopicsByPlan(p=>{
      const next = {...p};
      Object.keys(next).forEach(planId => { next[planId] = next[planId].map(t=>t.id===topicId?{...t,...payload}:t); });
      return next;
    });
    showToast("Topic updated.");
  };

  const updatePlanHeader = async (planId, form) => {
    const payload = { subject_name:form.subject_name, class:form.class, term_name:form.term_name, academic_year:form.academic_year };
    const { error } = await supabase.from("course_plans").update(payload).eq("id",planId);
    if (error) { showToast("Error: "+error.message, false); return; }
    setPlans(p=>p.map(x=>x.id===planId?{...x,...payload}:x));
    setEditingPlan(null);
    showToast("Plan details updated.");
  };

  const decide = async (plan, status, note) => {
    const notes = status==="rejected" ? (note||"") : (plan.notes||"");
    const { error } = await supabase.from("course_plans").update({
      status, notes, approved_at:new Date().toISOString(), approved_by_name:"Admin",
    }).eq("id",plan.id);
    if (error) { showToast("Error: "+error.message, false); return; }
    setPlans(p=>p.map(x=>x.id===plan.id?{...x,status,notes,approved_at:new Date().toISOString(),approved_by_name:"Admin"}:x));
    setDecidingFor(null); setDecidingType(null); setDecisionNote("");
    showToast(status==="approved" ? "Plan approved." : "Plan sent back to teacher.");
  };

  const teachers = [...new Set(plans.map(p=>p.teacher_name).filter(Boolean))].sort();
  const classNames = [...new Set(plans.map(p=>p.class).filter(Boolean))].sort();
  const filtered = plans.filter(p =>
    (statusFilter==="all" || p.status===statusFilter) &&
    (teacherFilter==="all" || p.teacher_name===teacherFilter) &&
    (classFilter==="all" || p.class===classFilter)
  );

  const submittedCount = plans.filter(p=>p.status==="submitted").length;
  const behindCount = plans.filter(p => paceFor(topicsByPlan[p.id]||[]).behind && p.status!=="rejected").length;

  // Aggregate completion stats per teacher/class, worst-pace first — gives admins a
  // single glance at who's behind instead of opening every plan individually.
  const overviewRows = React.useMemo(() => {
    const key = overviewBy === "teacher" ? "teacher_name" : "class";
    const groups = {};
    plans.forEach(p => {
      const name = p[key] || "Unknown";
      const topics = topicsByPlan[p.id]||[];
      const g = groups[name] ||= { name, planCount:0, total:0, done:0, overdue:0, behindPlans:0, approved:0 };
      g.planCount += 1;
      g.total += topics.length;
      g.done += topics.filter(t=>t.status==="completed").length;
      g.overdue += topics.filter(isOverdue).length;
      if (p.status==="approved") g.approved += 1;
      if (p.status!=="rejected" && paceFor(topics).behind) g.behindPlans += 1;
    });
    return Object.values(groups)
      .map(g => ({ ...g, pct: g.total ? Math.round((g.done/g.total)*100) : 0 }))
      .sort((a,b) => a.pct - b.pct || b.behindPlans - a.behindPlans);
  }, [plans, topicsByPlan, overviewBy]);

  const filterBtn = (id,label,count) => (
    <button onClick={()=>setStatusFilter(id)}
      style={{background:statusFilter===id?C.primary:C.white,color:statusFilter===id?C.white:C.text,border:`1px solid ${statusFilter===id?C.primary:C.border}`,borderRadius:9,padding:"7px 14px",fontWeight:700,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
      {label}
      {count!=null && <span style={{background:statusFilter===id?"rgba(255,255,255,.25)":C.primaryPale,color:statusFilter===id?C.white:C.primary,borderRadius:20,padding:"0 7px",fontSize:11,fontWeight:800}}>{count}</span>}
    </button>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <Toast toast={toast}/>
      <div style={{background:C.white,borderRadius:14,padding:"18px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 10px ${C.shadow}`}}>
        <div style={{fontWeight:900,fontSize:17,color:C.primary,marginBottom:4}}>📘 Course Plan Review</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Approve teachers' syllabus plans and keep an eye on pacing across the school.</div>

        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:14}}>
          {[
            {icon:"⏳",label:"Awaiting Review",val:submittedCount,col:C.amber,bg:C.amberPale},
            {icon:"⚠️",label:"Behind Schedule",val:behindCount,col:C.rose,bg:C.rosePale},
            {icon:"📋",label:"Total Plans",val:plans.length,col:C.blue,bg:C.bluePale},
          ].map(({icon,label,val,col,bg})=>(
            <div key={label} style={{background:bg,borderRadius:10,padding:"8px 14px",display:"flex",alignItems:"center",gap:9,border:`1px solid ${col}20`}}>
              <span style={{fontSize:18}}>{icon}</span>
              <div>
                <div style={{fontWeight:900,fontSize:20,color:col,lineHeight:1}}>{val}</div>
                <div style={{fontSize:10,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:.3}}>{label}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{display:"flex",gap:8,marginBottom:12}}>
          {[["plans","📋 Plans"],["overview","👩‍🏫 Overview"]].map(([id,label])=>(
            <button key={id} onClick={()=>setView(id)}
              style={{background:view===id?C.text:C.white,color:view===id?C.white:C.text,border:`1px solid ${view===id?C.text:C.border}`,borderRadius:9,padding:"7px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
              {label}
            </button>
          ))}
        </div>

        {view==="plans" && (
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            {filterBtn("submitted","⏳ Awaiting Review",submittedCount)}
            {filterBtn("approved","✅ Approved",plans.filter(p=>p.status==="approved").length)}
            {filterBtn("draft","📝 Drafts",plans.filter(p=>p.status==="draft").length)}
            {filterBtn("rejected","❌ Rejected",plans.filter(p=>p.status==="rejected").length)}
            {filterBtn("all","All",plans.length)}
            {teachers.length>0 && (
              <select value={teacherFilter} onChange={e=>setTeacherFilter(e.target.value)}
                style={{marginLeft:"auto",padding:"6px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,background:C.white}}>
                <option value="all">All Teachers</option>
                {teachers.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            )}
            {classNames.length>0 && (
              <select value={classFilter} onChange={e=>setClassFilter(e.target.value)}
                style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,background:C.white}}>
                <option value="all">All Classes</option>
                {classNames.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
        )}

        {view==="overview" && (
          <div style={{display:"flex",gap:8}}>
            {[["teacher","By Teacher"],["class","By Class"]].map(([id,label])=>(
              <button key={id} onClick={()=>setOverviewBy(id)}
                style={{background:overviewBy===id?C.primary:C.white,color:overviewBy===id?C.white:C.text,border:`1px solid ${overviewBy===id?C.primary:C.border}`,borderRadius:9,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {view==="overview" && (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {overviewRows.length===0 && (
            <div style={{background:C.white,borderRadius:12,padding:30,textAlign:"center",color:C.muted,fontSize:13,border:`1px solid ${C.border}`}}>
              No course plans yet.
            </div>
          )}
          {overviewRows.map(row => (
            <div key={row.name} style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,padding:"14px 18px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
              <div style={{minWidth:160,flex:1}}>
                <div style={{fontWeight:800,fontSize:14,color:C.text}}>{row.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{row.planCount} plan{row.planCount!==1?"s":""} · {row.approved} approved</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,minWidth:140}}>
                <div style={{flex:1,background:"#f3f4f6",borderRadius:5,height:9,overflow:"hidden",minWidth:90}}>
                  <div style={{background:row.behindPlans>0?C.rose:C.primary,height:"100%",width:`${row.pct}%`}}/>
                </div>
                <span style={{fontSize:12,fontWeight:800,color:C.text,minWidth:34}}>{row.pct}%</span>
              </div>
              <div style={{fontSize:11,color:C.muted}}>{row.done}/{row.total} topics done</div>
              {row.overdue>0 && (
                <span style={{background:C.rosePale,color:C.rose,border:"1px solid #fecaca",borderRadius:6,padding:"2px 9px",fontSize:11,fontWeight:700}}>
                  ⚠️ {row.overdue} overdue
                </span>
              )}
              {row.behindPlans>0 && (
                <span style={{background:C.rosePale,color:C.rose,border:"1px solid #fecaca",borderRadius:6,padding:"2px 9px",fontSize:11,fontWeight:700}}>
                  {row.behindPlans} plan{row.behindPlans!==1?"s":""} behind
                </span>
              )}
              <button onClick={()=>{
                  setView("plans"); setStatusFilter("all");
                  if (overviewBy==="teacher") { setTeacherFilter(row.name); setClassFilter("all"); }
                  else { setClassFilter(row.name); setTeacherFilter("all"); }
                }}
                style={{background:C.bg,color:C.text,border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                View Plans →
              </button>
            </div>
          ))}
        </div>
      )}

      {view==="plans" && loading && <div style={{color:C.muted,fontSize:13,padding:16}}>Loading…</div>}
      {view==="plans" && !loading && filtered.length===0 && (
        <div style={{background:C.white,borderRadius:12,padding:30,textAlign:"center",color:C.muted,fontSize:13,border:`1px solid ${C.border}`}}>
          No plans match this filter.
        </div>
      )}

      {view==="plans" && filtered.map(plan => {
        const topics = topicsByPlan[plan.id]||[];
        const st = PLAN_STATUS_META[plan.status]||PLAN_STATUS_META.draft;
        const open = expanded===plan.id;
        const isEditingHeader = editingPlan===plan.id;
        return (
          <div key={plan.id} style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
            <div onClick={()=>setExpanded(open?null:plan.id)}
              style={{padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",cursor:"pointer"}}>
              <div>
                <div style={{fontWeight:800,fontSize:14,color:C.text}}>{plan.subject_name} · {plan.class}</div>
                <div style={{fontSize:11,color:C.muted}}>{plan.teacher_name} · {plan.term_name} · {plan.academic_year}</div>
              </div>
              <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                <PaceBadge topics={topics}/>
                <MiniBar topics={topics}/>
                <span style={{background:st.bg,color:st.fg,border:`1px solid ${st.bd}`,borderRadius:6,padding:"2px 9px",fontSize:11,fontWeight:700}}>
                  {st.icon} {st.label}
                </span>
                <button onClick={(e)=>{e.stopPropagation(); setExpanded(plan.id); setEditingPlan(isEditingHeader?null:plan.id);}}
                  title="Edit plan details"
                  style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"2px 8px",fontSize:12,cursor:"pointer"}}>
                  ✏️
                </button>
                <span style={{fontSize:13,color:C.muted}}>{open?"▲":"▼"}</span>
              </div>
            </div>
            {open && (
              <div style={{padding:"0 18px 18px",display:"flex",flexDirection:"column",gap:8}}>
                {isEditingHeader ? (
                  <NewPlanForm subjects={[]} classes={[]} initial={plan} title="Edit Plan Details" submitLabel="Save Changes"
                    onCreate={(form)=>updatePlanHeader(plan.id, form)} onCancel={()=>setEditingPlan(null)}/>
                ) : (
                  <>
                    {topics.length===0 && <div style={{fontSize:12,color:C.muted,fontStyle:"italic"}}>No topics added yet.</div>}
                    {topics.length>0 && <ProgressBar topics={topics}/>}
                    {topics.map(t => <TopicRow key={t.id} topic={t} editable={false} canEditContent onEdit={updateTopic}/>)}
                    {plan.status==="submitted" && decidingFor===plan.id && decidingType==="rejected" && (
                      <div style={{display:"flex",flexDirection:"column",gap:8,padding:"10px 12px",background:C.rosePale,border:"1px solid #fecaca",borderRadius:9,marginTop:6}}>
                        <div style={{fontSize:12,fontWeight:700,color:C.rose}}>Reason for rejecting (shown to teacher)</div>
                        <textarea value={decisionNote} onChange={e=>setDecisionNote(e.target.value)} rows={2} autoFocus
                          placeholder="What needs to change before this can be approved?"
                          style={{padding:"7px 10px",borderRadius:7,border:"1px solid #fecaca",fontSize:12,boxSizing:"border-box",resize:"vertical"}}/>
                        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                          <button onClick={()=>{setDecidingFor(null);setDecidingType(null);setDecisionNote("");}}
                            style={{background:"none",border:`1px solid ${C.border}`,borderRadius:7,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                            Cancel
                          </button>
                          <button onClick={()=>decide(plan,"rejected",decisionNote.trim())}
                            style={{background:"#dc2626",color:C.white,border:"none",borderRadius:7,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                            ❌ Confirm Send Back
                          </button>
                        </div>
                      </div>
                    )}
                    {plan.status==="submitted" && decidingFor===plan.id && decidingType==="approved" && (
                      <div style={{display:"flex",flexDirection:"column",gap:8,padding:"10px 12px",background:C.primaryPale,border:`1px solid ${C.border}`,borderRadius:9,marginTop:6}}>
                        <div style={{fontSize:12,color:C.text}}>Approve <b>"{plan.subject_name} · {plan.class}"</b> course plan?</div>
                        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                          <button onClick={()=>{setDecidingFor(null);setDecidingType(null);}}
                            style={{background:"none",border:`1px solid ${C.border}`,borderRadius:7,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                            Cancel
                          </button>
                          <button onClick={()=>decide(plan,"approved",null)}
                            style={{background:C.primary,color:C.white,border:"none",borderRadius:7,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                            ✅ Confirm Approve
                          </button>
                        </div>
                      </div>
                    )}
                    {plan.status==="submitted" && decidingFor!==plan.id && (
                      <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:6}}>
                        <button onClick={()=>{setDecidingFor(plan.id);setDecidingType("rejected");setDecisionNote("");}}
                          style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                          ❌ Send Back
                        </button>
                        <button onClick={()=>{setDecidingFor(plan.id);setDecidingType("approved");}}
                          style={{background:C.primary,color:C.white,border:"none",borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                          ✅ Approve
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
